import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Box,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 600,
  },
  formControl: {
    width: '100%',
  },
  helperText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

const CreateTaskModal = ({
  open,
  onClose,
  onSubmit,
  files,
  services,
  regardings,
  vendors,
  letterTemplates,
  isLoading,
  allEnums
}) => {
  const classes = useStyles();

  const taskTypes =
    allEnums?.taskTypes && allEnums.taskTypes.length > 0
      ? allEnums.taskTypes.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const taskImportances =
    allEnums?.taskImportances && allEnums.taskImportances.length > 0
      ? allEnums.taskImportances.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-111111111111', title: '' }];

  const [formData, setFormData] = useState({
    fileId: null,
    taskTypeId: null,
    taskText: '',
    dueDate: '',
    importanceId: 2, // Default to Normal
    serviceId: null,
    regardingId: null,
    vendorId: null,
    letterTemplateId: null,
  });

  const [errors, setErrors] = useState({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setFormData({
        fileId: null,
        taskTypeId: null,
        taskText: '',
        dueDate: '',
        importanceId: 2,
        serviceId: null,
        regardingId: null,
        vendorId: null,
        letterTemplateId: null,
      });
      setErrors({});
    }
  }, [open]);

  const handleFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const isVendorTask = () => {
    return [2, 4, 7, 8].includes(formData.taskTypeId);
  };

  const isLetterTask = () => {
    return [5, 7].includes(formData.taskTypeId);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fileId) newErrors.fileId = 'File is required';
    if (!formData.taskTypeId) newErrors.taskTypeId = 'Task type is required';
    if (!formData.taskText || !formData.taskText.trim()) newErrors.taskText = 'Task description is required';
    if (!formData.dueDate) newErrors.dueDate = 'Due date is required';
    if (!formData.importanceId) newErrors.importanceId = 'Importance is required';
    if (!formData.serviceId) newErrors.serviceId = 'Service is required';
    if (!formData.regardingId) newErrors.regardingId = 'Regarding is required';

    // Conditional validation
    if (isVendorTask() && !formData.vendorId) {
      newErrors.vendorId = 'Vendor is required for vendor tasks';
    }
    if (isLetterTask() && !formData.letterTemplateId) {
      newErrors.letterTemplateId = 'Letter template is required for letter tasks';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(formData);
    }
  };

  const selectedFile = files.find(f => f.fileId === formData.fileId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Manual Task</DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Grid container spacing={2}>
          {/* File Selection */}
          <Grid item xs={12}>
            <Autocomplete
              options={files}
              getOptionLabel={(option) => `${option.fileNumber || 'N/A'} - ${option.transfereeName || option.eeFullName || 'N/A'}`
                + (option.coveringForName ? ` (Covering for ${option.coveringForName})` : '')}
              value={selectedFile || null}
              onChange={(event, newValue) => handleFieldChange('fileId', newValue?.fileId || null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="File / Employee *"
                  error={!!errors.fileId}
                  helperText={errors.fileId || 'Search by file number or employee name'}
                  size="small"
                />
              )}
            />
          </Grid>

          {/* Task Type */}
          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl} error={!!errors.taskTypeId} size="small">
              <InputLabel>Task Type *</InputLabel>
              <Select
                value={formData.taskTypeId || ''}
                onChange={(e) => handleFieldChange('taskTypeId', e.target.value)}
              >
                {taskTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.title}
                  </MenuItem>
                ))}
              </Select>
              {errors.taskTypeId && (
                <Typography variant="caption" color="error">{errors.taskTypeId}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Importance */}
          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl} error={!!errors.importanceId} size="small">
              <InputLabel>Importance *</InputLabel>
              <Select
                value={formData.importanceId || ''}
                onChange={(e) => handleFieldChange('importanceId', e.target.value)}
              >
                {taskImportances.map((level) => (
                  <MenuItem key={level.id} value={level.id}>
                    {level.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Task Description */}
          <Grid item xs={12}>
            <TextField
              label="Task Description *"
              multiline
              rows={3}
              fullWidth
              size="small"
              value={formData.taskText}
              onChange={(e) => handleFieldChange('taskText', e.target.value)}
              error={!!errors.taskText}
              helperText={errors.taskText || 'What the RC should do'}
              placeholder="E.g., Call employee to confirm delivery date and time window"
            />
          </Grid>

          {/* Due Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Due Date *"
              type="date"
              fullWidth
              size="small"
              value={formData.dueDate}
              onChange={(e) => handleFieldChange('dueDate', e.target.value)}
              error={!!errors.dueDate}
              helperText={errors.dueDate}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Service */}
          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl} error={!!errors.serviceId} size="small">
              <InputLabel>Service *</InputLabel>
              <Select
                value={formData.serviceId || ''}
                onChange={(e) => handleFieldChange('serviceId', e.target.value)}
              >
                {services.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.title}
                  </MenuItem>
                ))}
              </Select>
              {errors.serviceId && (
                <Typography variant="caption" color="error">{errors.serviceId}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Regarding */}
          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl} error={!!errors.regardingId} size="small">
              <InputLabel>Regarding *</InputLabel>
              <Select
                value={formData.regardingId || ''}
                onChange={(e) => handleFieldChange('regardingId', e.target.value)}
              >
                {regardings.map((regarding) => (
                  <MenuItem key={regarding.id} value={regarding.id}>
                    {regarding.title}
                  </MenuItem>
                ))}
              </Select>
              {errors.regardingId && (
                <Typography variant="caption" color="error">{errors.regardingId}</Typography>
              )}
            </FormControl>
          </Grid>

          {/* Vendor (conditional) */}
          {isVendorTask() && (
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={vendors}
                getOptionLabel={(option) => option.companyName || option.vendorName || ''}
                value={vendors.find(v => v.vendorId === formData.vendorId) || null}
                onChange={(event, newValue) => handleFieldChange('vendorId', newValue?.vendorId || null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor *"
                    error={!!errors.vendorId}
                    helperText={errors.vendorId}
                    size="small"
                  />
                )}
              />
            </Grid>
          )}

          {/* Letter Template (conditional) */}
          {isLetterTask() && (
            <Grid item xs={12}>
              <FormControl className={classes.formControl} error={!!errors.letterTemplateId} size="small">
                <InputLabel>Letter Template *</InputLabel>
                <Select
                  value={formData.letterTemplateId || ''}
                  onChange={(e) => handleFieldChange('letterTemplateId', e.target.value)}
                >
                  {letterTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.letterTemplateId && (
                  <Typography variant="caption" color="error">{errors.letterTemplateId}</Typography>
                )}
              </FormControl>
            </Grid>
          )}
        </Grid>

        <Box className={classes.helperText}>
          <Typography variant="caption">
            * Required field
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={isLoading}
        >
          Create Task
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateTaskModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  files: PropTypes.arrayOf(PropTypes.shape({
    fileId: PropTypes.number,
    fileNumber: PropTypes.string,
    transfereeName: PropTypes.string,
    eeFullName: PropTypes.string,
    coveringForName: PropTypes.string,
  })),
  services: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    description: PropTypes.string,
  })),
  regardings: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    description: PropTypes.string,
  })),
  vendors: PropTypes.arrayOf(PropTypes.shape({
    vendorId: PropTypes.number,
    companyName: PropTypes.string,
    vendorName: PropTypes.string,
  })),
  letterTemplates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  isLoading: PropTypes.bool,
};

CreateTaskModal.defaultProps = {
  files: [],
  services: [],
  regardings: [],
  vendors: [],
  letterTemplates: [],
  isLoading: false,
};

export default CreateTaskModal;
